"use client";

import { useBuilder } from "@/context/BuilderContext";
import { formatPrice } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { Check, Layers, Grid3X3, CircleDot, Maximize } from "lucide-react";
import { useTranslations } from "next-intl";
import { useBuilderOptions } from "@/hooks/useBuilderOptions";

export function StepBaseType() {
  const { selections, setOption } = useBuilder();
  const selected = selections.base_type;
  const t = useTranslations("builder");
  const { options } = useBuilderOptions();

  // Build price lookup from DB options
  const dbPrices = new Map(
    (options.base_type || []).map((o) => [o.value, o.price])
  );

  const BASE_TYPES = [
    {
      value: "Lace Top",
      price: dbPrices.get("Lace Top") ?? 0,
      label: t("laceTopName"),
      description: t("laceTopDesc"),
      detail: t("laceTopDetail"),
      icon: Layers,
    },
    {
      value: "Silk Top",
      price: dbPrices.get("Silk Top") ?? 400,
      label: t("silkTopName"),
      description: t("silkTopDesc"),
      detail: t("silkTopDetail"),
      icon: Grid3X3,
    },
    {
      value: "Monofilament",
      price: dbPrices.get("Monofilament") ?? 200,
      label: t("monofilamentName"),
      description: t("monofilamentDesc"),
      detail: t("monofilamentDetail"),
      icon: CircleDot,
    },
    {
      value: "Full Lace",
      price: dbPrices.get("Full Lace") ?? 600,
      label: t("fullLaceName"),
      description: t("fullLaceDesc"),
      detail: t("fullLaceDetail"),
      icon: Maximize,
    },
  ];

  const guides = [
    t("baseTypeGuideLaceFront"),
    t("baseTypeGuideFullLace"),
    t("baseTypeGuideSilkTop"),
    t("baseTypeGuideMono"),
  ];

  return (
    <div className="space-y-6 pt-2">
      {/* Contextual Guidance */}
      <div className="rounded-lg border border-brand-dark/5 bg-brand-cream/30 p-4 sm:p-5">
        <ul className="space-y-2">
          {guides.map((guide, idx) => (
            <li key={idx} className="flex items-start gap-2 text-xs leading-relaxed text-foreground/70">
              <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-brand-gold" />
              {guide}
            </li>
          ))}
        </ul>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {BASE_TYPES.map((option) => {
        const isSelected = selected === option.value;
        const Icon = option.icon;

        return (
          <button
            key={option.value}
            onClick={() => setOption("base_type", option.value, option.price)}
            className={cn(
              "relative text-start p-4 sm:p-6 rounded-lg border-2 transition-all duration-300 group active:scale-[0.98]",
              "hover:shadow-md hover:border-brand-gold-light",
              isSelected
                ? "border-brand-gold bg-brand-gold/[0.03] shadow-md"
                : "border-theme-border bg-surface"
            )}
          >
            {/* Selected checkmark */}
            {isSelected && (
              <div className="absolute top-4 end-4 w-6 h-6 bg-brand-gold rounded-full flex items-center justify-center animate-scale-in">
                <Check className="w-3.5 h-3.5 text-white" />
              </div>
            )}

            {/* Icon */}
            <div
              className={cn(
                "w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-colors duration-300",
                isSelected
                  ? "bg-brand-gold/10 text-brand-gold"
                  : "bg-brand-cream text-foreground-muted group-hover:bg-brand-gold/5 group-hover:text-brand-gold-light"
              )}
            >
              <Icon className="w-6 h-6" />
            </div>

            {/* Content */}
            <h3 className="font-display text-lg text-foreground mb-1">
              {option.label}
            </h3>
            <p className="text-sm text-brand-muted leading-relaxed mb-3">
              {option.description}
            </p>

            {/* Footer */}
            <div className="flex items-center justify-between pt-3 border-t border-theme-border/60">
              <span className="text-xs text-brand-muted tracking-wider uppercase">
                {option.detail}
              </span>
              <span
                className={cn(
                  "text-sm font-medium",
                  option.price === 0 ? "text-green-600" : "text-foreground"
                )}
              >
                {option.price === 0 ? t("included") : `+${formatPrice(option.price)}`}
              </span>
            </div>
          </button>
        );
      })}
      </div>
    </div>
  );
}
